import type {
  CreateReportPayload,
  ReportTemplate,
} from 'api/analytics-reports/types';
import type { Dayjs } from 'dayjs';
import type { QuickReportScope } from 'app/modules/analytics/modules/reports/constants/quickReportDefaults';
import { buildProfilesFromCards } from 'app/modules/analytics/modules/reports/utils/buildCreateReportPayload';
import { mapCreateReportPeriodPreset } from 'app/modules/analytics/modules/reports/utils/mapCreateReportPeriodPreset';
import type {
  ReportProfileEditorState,
  ReportSectionMetricsContext,
} from 'app/modules/analytics/modules/reports/types/ReportProfileEditorState';
import { collectAvailableReportMetricsFromTemplates } from 'app/modules/analytics/modules/reports/utils/collectAvailableReportMetricsFromTemplates';
import type { ProfileGroup } from 'types/Page';

export interface BuildQuickReportPayloadInput {
  scope: QuickReportScope;
  name: string;
  profileCards: ReportProfileEditorState[];
  datePreset: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  isGroupSelected: boolean;
  selectedProfileGroup: ProfileGroup | null;
  template: ReportTemplate;
  templateCode: string;
}

export function buildQuickReportPayload(
  input: BuildQuickReportPayloadInput,
): CreateReportPayload {
  const periodPreset = mapCreateReportPeriodPreset(input.datePreset);
  const {
    performance: availablePerformanceMetrics,
    content: availableContentMetrics,
  } = collectAvailableReportMetricsFromTemplates([input.template]);
  const sectionMetricsContext: ReportSectionMetricsContext = {
    template: input.template,
    availablePerformanceMetrics,
    availableContentMetrics,
  };
  const payload: CreateReportPayload = {
    name: input.name.trim(),
    period_preset: periodPreset,
    profiles: buildProfilesFromCards(input.profileCards, sectionMetricsContext),
  };

  if (input.scope === 'full') {
    payload.template = input.templateCode;
  }

  if (periodPreset === 'custom') {
    payload.period_from = input.startDate.format('YYYY-MM-DD');
    payload.period_to = input.endDate.format('YYYY-MM-DD');
  }

  if (input.isGroupSelected && input.selectedProfileGroup) {
    payload.preset_id = input.selectedProfileGroup.id;
  }

  return payload;
}
